import React, {useCallback, useMemo} from "react";
import {Col} from "reactstrap";
import Button from "../../base/button/button";
import Form from "../../base/form";
import {DatePickerForm} from "../../base/date-picker";
import {DropdownForm} from "../../base/dropdown/dropdown";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';
import {InventoryTransferStatusList} from "../../../../core/constants/enums";
import {useTranslation} from "react-i18next";

const formValues = {
    statusNames: 'statusNames',
    submitPeriodStart: 'submitPeriodStart',
    submitPeriodEnd: 'submitPeriodEnd',
};

const schema = Yup.object().shape({
    [formValues.statusNames]: Yup.array().of(Yup.string()).nullable(),
    [formValues.submitPeriodStart]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formValues.submitPeriodEnd), ValidateMessages.lessThan('End Date'))
        .nullable(),
    [formValues.submitPeriodEnd]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formValues.submitPeriodStart), ValidateMessages.greaterThan('Start Date'))
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const InventoryTransferListSearchSection = ({searchData}) => {
    const {t} = useTranslation('components', {keyPrefix: 'inventoryTransferListSearchSection'});

    const statusOptions = useMemo(() => (
        InventoryTransferStatusList.map((status) => ({
            label: status,
            value: status,
            id: status,
        }))
    ), []);

    const initialValues = useMemo(() => ({
        [formValues.statusNames]: searchData.filters?.statusNames ?? [],
        [formValues.submitPeriodStart]: searchData.filters?.submitPeriodStart
            ? DateUtils.serverToUTC(searchData.filters?.submitPeriodStart)
            : undefined,
        [formValues.submitPeriodEnd]: searchData.filters?.submitPeriodEnd
            ? DateUtils.serverToUTC(searchData.filters?.submitPeriodEnd)
            : undefined,
    }), [searchData.filters]);

    /**
     * Applies search filters from the form.
     */
    const onSubmit = useCallback((values) => {
        searchData.setFilters((prevState) => ({
            ...prevState,
            statusNames: values[formValues.statusNames] ?? [],
            submitPeriodStart: values[formValues.submitPeriodStart]
                ? DateUtils.UTCToServer(values[formValues.submitPeriodStart]).format(DateUtils.withoutOffsetFormat)
                : undefined,
            submitPeriodEnd: values[formValues.submitPeriodEnd]
                ? DateUtils.UTCToServer(values[formValues.submitPeriodEnd]).format(DateUtils.withoutOffsetFormat)
                : undefined,
        }));
    }, [searchData]);

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            className={'d-flex flex-row flex-wrap'}
            render={({submitting, values}) => {
                const colClassName = 'px-2 mb-4';
                return (
                    <>
                        <Col xs={12} className={colClassName}>
                            <DropdownForm
                                label={t('statuses')}
                                name={formValues.statusNames}
                                multiple
                                data={statusOptions}
                                renderMultipleSelectValue={(status, index, arrayLength) => (
                                    <p key={status} className={'mr-2 mt-2'}>{status}{index < arrayLength - 1 ? ',' : ''}</p>
                                )}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DatePickerForm
                                name={formValues.submitPeriodStart}
                                label={t('submitPeriodStart')}
                                required={required[formValues.submitPeriodStart]}
                                maxDate={values[formValues.submitPeriodEnd]}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DatePickerForm
                                name={formValues.submitPeriodEnd}
                                label={t('submitPeriodEnd')}
                                required={required[formValues.submitPeriodEnd]}
                                minDate={values[formValues.submitPeriodStart]}
                            />
                        </Col>
                        <Col xs={12} className={'px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'}>
                            <Button
                                disabled={!!submitting || searchData.loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className={'py-3 px-8'}
                            >
                                {
                                    submitting || searchData.loading
                                        ? t('searching')
                                        : t('search')
                                }
                            </Button>
                        </Col>
                    </>
                );
            }}
        />
    );
};

export default InventoryTransferListSearchSection;
